import React from 'react'
import { useState } from 'react'
import { observer } from 'mobx-react'
import { observable } from 'mobx'
import { Dropdown, Form, Button, Row, InputGroup } from 'react-bootstrap'
import { NuskinTaskIcon } from 'src/views/components/Header/Components/CartIcon/NuskinTaskIcon'
import {
  IoIosSearch as SearchIcon,
  IoIosClose as CloseIcon,
  IoMdAddCircleOutline as CircleAdd,
} from 'react-icons/io'
import { BsPlus } from 'react-icons/bs'
import loadable from '@loadable/component'
import { Link } from 'react-router-dom'
import debounce from 'lodash/debounce'
import {
  IS_BROWSER,
  minimumSearchCharLength,
  APPConfig,
  convertToBoolean,
} from 'config/appConfig'
import { pageNames } from 'src/routes/pathParams'
import { catalogContainer, customerContainer } from 'src/models'
import {
  application,
  getLocalStorage,
  setLocalStorage,
  getImageFromProperty,
  trackSearchSuggestions,
  trackRecentSearch,
  trackPredictiveSearch,
  trackSearchResult,
  trackPredictiveSearchEvent,
  trackRecentSearchEvent,
  i18nTranslate,
  getLiveEventStatus,
  getDeviceType,
} from 'src/utils'
import TagManager from 'react-gtm-module'
import { catalogDeps, searchDeps } from 'src/deps'
import { ImageComponent, quickViewState, QuickView } from 'src/views/components'
import { searchState } from './SearchState'
import { NuskinPlusCartIcon } from '../CartIcon/NuskinCartIcon'
import {
  shouldRedirectToPdp,
  shouldRedirectBasedOnProduct,
} from '../../../CatalogViews/ProductCard/ProductCardUtils'
import './styles.scss'

// The redesigned search styles are only needed when enableNewHeaderNavigation
// is on, so they stay in their own chunk. Loading them through loadable (rather
// than an import() in componentDidMount) lets the server-side ChunkExtractor
// inline the CSS into the HTML, so the server-rendered search box already has
// its final look instead of restyling once the client has downloaded it.
// A failed download must not take the header down with it (loadable throws on
// load errors), so it resolves to an empty module instead.
const RedesignStyles = loadable.lib(
  () =>
    import('./redesign.scss').catch(error => {
      console.warn('Failed to load redesign styles', error)
      return {}
    }),
  { fallback: null }
)

const RenderForm = React.forwardRef((props, ref) => {
  const {
    value = '',
    onFocus,
    onChange,
    placeHolder = '',
    removeSearch,
    isNuskinHeader,
    onClick,
    onKeyDown,
    onMouseDown,
  } = props
  const [isFocused, setIsFocused] = useState(false)
  const isRedesignEnabled = convertToBoolean(
    APPConfig?.getAppConfig()?.enableNewHeaderNavigation
  )
  let searchData = customerContainer.searchData
    ? JSON.parse(JSON.stringify(customerContainer.searchData))
    : null
  let placeHolderText = ''
  if (isRedesignEnabled && searchData) {
    placeHolderText = !isFocused
      ? searchData?.product_search
      : searchData?.popular_search
  } else if (isNuskinHeader) {
    placeHolderText = i18nTranslate('header.globalSearch', 'Search', {
      nameSpace: 'ssr-resource',
    })
  } else {
    placeHolderText =
      placeHolder ||
      i18nTranslate(
        'header.globalSearchProduct',
        'Search for products, devices and information',
        { nameSpace: 'ssr-resource' }
      )
  }
  const handleKeyDownEscape = React.useCallback(
    event => {
      if (event.key === 'Escape') {
        removeSearch()
      }
    },
    [removeSearch]
  )
  const escapeKeyExit = React.useCallback(() => {
    document.addEventListener('keydown', handleKeyDownEscape)
  }, [handleKeyDownEscape])
  const handleFocus = e => {
    setIsFocused(true)
    if (onFocus) onFocus(e)
    escapeKeyExit()
  }
  const handleBlur = () => {
    setIsFocused(false)
    document.removeEventListener('keydown', handleKeyDownEscape)
  }
  const attributes = {
    value,
    onFocus,
    onChange,
    onClick,
    onKeyDown,
    onMouseDown,
    onFocus: handleFocus,
    onBlur: handleBlur,
  }
  return (
    <div className="position-relative flex-grow-1 ">
      <Form.Control
        ref={ref}
        type="text"
        className="qa-search-input"
        placeholder={placeHolderText}
        {...attributes}
        data-testid="qa-search-input"
        aria-label={i18nTranslate('input.searchInput', 'search input textbox', {
          nameSpace: 'ssr-resource',
        })}
      />
    </div>
  )
})

RenderForm.displayName = 'RenderForm'

const SearchInputGroupContent = ({
  searchValue,
  isNuskinHeader,
  isRedesignEnabled,
  onClearInput,
  onSubmit,
  clearInputAriaLabel,
  navigationSearchAriaLabel,
  children,
}) => {
  const searchIconClass = isRedesignEnabled ? 'search-box-svg' : 'search-icon'
  const submitHandlerProps = !customerContainer.isGuestB2B
    ? { onClick: onSubmit }
    : {}

  return (
    <>
      {/*
       * Render the search button before the input so DOM/tab order matches the
       * visual order (search -> input -> clear). Avoids CSS `order` reordering,
       * which desynced keyboard focus order from the visual layout. (ADA)
       */}
      <InputGroup.Append className="d-flex align-items-center search-icon-button border-0">
        {isNuskinHeader ? (
          <Button
            aria-label={i18nTranslate('searchBar.placeholder', 'Search', {
              nameSpace: 'ssr-resource',
            })}
            className="search-icon-inner-button border-0"
            variant="btn-link"
            {...submitHandlerProps}
            style={{ backgroundColor: 'unset !important' }}
            data-testid="qa-search-button">
            <SearchIcon
              className={searchIconClass}
              size="1.25rem"
              aria-label={navigationSearchAriaLabel}
              background-color="transparent"
            />
          </Button>
        ) : (
          <SearchIcon
            className={searchIconClass}
            {...submitHandlerProps}
            size="1.25rem"
            background-color="transparent"
            data-testid="qa-search-icon"
            aria-label={i18nTranslate('icons.searchicon', 'search icon', {
              nameSpace: 'ssr-resource',
            })}
          />
        )}
      </InputGroup.Append>
      {children}
      <div className="d-flex align-items-center search-clear-wrapper">
        {searchValue && (
          <Button
            size="sm"
            variant="light"
            onClick={onClearInput}
            className={isNuskinHeader ? 'nu-cancel-search' : 'search-remove'}
            data-testid="qa-search-clear">
            <CloseIcon
              size={isRedesignEnabled ? '1.5rem' : '1rem'}
              className="text-dark"
              aria-label={clearInputAriaLabel}
              data-testid="qa-search-clear-icon"
            />
          </Button>
        )}
      </div>
    </>
  )
}

@observer
class SearchBox extends React.Component {
  static defaultProps = {
    className: '',
  }

  state = { showAllChips: false }
  // Use enableNewHeaderNavigation to control redesigned search styling
  isRedesignEnabled = convertToBoolean(
    APPConfig?.getAppConfig()?.enableNewHeaderNavigation
  )

  isActive = false
  isListening = false
  refSearchInput = React.createRef()
  dropDownSelectedValue = ''
  userEnteredValue = ''
  suggestionResponse = {}
  latestSuggestionsPromise = Promise.resolve()

  @observable isSuggestionVisible = false
  @observable searchValue = ''
  @observable suggestionList = []
  @observable limitSuggestion = []
  @observable didYouMean = []
  @observable recentSearch = getLocalStorage('recentSearch') || []
  @observable searchLimit = 6
  @observable showMore = false
  @observable indexLimit = 6
  @observable limitRecentSearch = 10
  @observable isSearchFocused = false
  @observable dropDownSelectedIdx = -1
  @observable hoverIndex = null
  @observable hasUserTyped = false
  @observable searchAnnouncement = ''

  enableLiveEvent = getLiveEventStatus()
  /**
   * Why we need ref?
   * here we need to find that user has clicked outside the search box and suggestion dropDown
   * so the current dom has been taken through ref,
   * and finding the dom which is clicked is outside or inside the parent
   * Reference Link:
   * @see https://medium.com/@pitipatdop/little-neat-trick-to-capture-click-outside-react-component-5604830beb7f
   * @see https://stackoverflow.com/questions/32553158/detect-click-outside-react-component
   * below functions are for the boundary clicking check
   */

  componentDidMount() {
    const pathname = this.props.location?.pathname || ''
    if (
      pathname.includes(`${pageNames.search}/`) &&
      !this.props?.nuskinReadOnly
    ) {
      let searchTermParam
      try {
        searchTermParam = decodeURIComponent(
          catalogDeps?.queryParam?.searchTerm
        )
      } catch {
        searchTermParam = catalogDeps?.queryParam?.searchTerm
      }

      this.searchValue = this.props.searchCleared ? '' : searchTermParam
    }

    if (typeof window === 'object') {
      window.addEventListener('keydown', this.handleGlobalKeyDown, true)
    }
  }

  componentWillUnmount() {
    if (typeof window === 'object') {
      window.removeEventListener('keydown', this.handleGlobalKeyDown, true)
    }
    this.announceSearchResults.cancel()
  }

  clearAnnouncement = () => {
    this.announceSearchResults.cancel()
    this.searchAnnouncement = ''
  }

  announceSearchResults = debounce(async () => {
    await this.latestSuggestionsPromise
    const term = this.searchValue
    if (!term) {
      this.searchAnnouncement = ''
      return
    }
    const count = this.suggestionList?.length || 0
    this.searchAnnouncement =
      count > 0
        ? i18nTranslate(
            'search.resultsAnnouncement',
            `${count} results are displayed for searching ${term}`,
            { nameSpace: 'ssr-resource' }
          )
        : i18nTranslate(
            'search.noResultsAnnouncement',
            `No products found for '${term}'`,
            { nameSpace: 'ssr-resource' }
          )
  }, 500)

  componentDidUpdate(prevProps) {
    const pathname = this.props?.location?.pathname || '/'
    if (prevProps?.location?.pathname !== pathname) {
      if (pathname?.indexOf(pageNames.search) === -1) {
        this.searchValue = ''
      }
    }
  }

  setRef = node => {
    this.searchContainerNode = node
  }

  enableListener = () => {
    if (this.isActive === true && this.isListening === false) {
      if (typeof window === 'object') {
        window.addEventListener('click', this.handleOutsideClick, true)
        this.isListening = true
      }
    }
  }

  disableListener = () => {
    if (this.isListening === true) {
      if (typeof window === 'object') {
        window.removeEventListener('click', this.handleOutsideClick, true)
        this.isListening = false
      }
    }
  }

  handleBoundaryFocus = () => {
    this.isActive = false
    this.isSearchFocused = true
    searchState.setSearchFocused(true)
    this.isSuggestionVisible = true
  }

  handleBoundaryBlur = event => {
    this.isActive = true
    this.enableListener()
    if (
      event?.currentTarget &&
      event?.relatedTarget &&
      !event?.currentTarget?.contains(event?.relatedTarget)
    ) {
      this.isSearchFocused = false
      searchState.setSearchFocused(false)
      if (!application.isMobile) {
        this.isSuggestionVisible = false
      }
    }
  }

  handleOutsideClick = (event, isToCloseSuggestion = false) => {
    this.dropDownSelectedIdx = -1
    if (
      event?.target?.id !== 'app-root' &&
      ((this.searchContainerNode &&
        !this.searchContainerNode?.contains(event.target)) ||
        isToCloseSuggestion)
    ) {
      if (!application.isMobile) {
        this.isSuggestionVisible = false
      }
      this.isSearchFocused = false
      this.isActive = false
      searchState.setSearchFocused(false)
      this.disableListener()

      this.searchValue = ''
      this.userEnteredValue = ''
      this.dropDownSelectedValue = ''
      if (this.refSearchInput) {
        this.refSearchInput.value = ''
      }
      this.resetData()
      this.didYouMean = []
      this.hoverIndex = null
      this.clearAnnouncement()
      this.props.clearSearchModal(true)
    }
  }

  handleDropdownVisibility = () => {
    const isVisible =
      this.isSearchFocused ||
      this.suggestionList?.length > 0 ||
      this.didYouMean?.length > 0 ||
      this.recentSearch?.length > 0
        ? true
        : false
    return isVisible
  }

  getSuggestionList = productList => {
    const enableSubscriptionVariantSearch = convertToBoolean(
      APPConfig?.getAppConfig()?.enableSubscriptionVariantSearch
    )

    const processedProducts = !enableSubscriptionVariantSearch
      ? productList
      : catalogDeps.splitSkuBasedProduct(productList)

    const uniqueProducts = []
    const seenIds = new Set()
    processedProducts?.forEach(product => {
      const productId = product?.identifier || product?.productId
      const productName = product?.properties?.name || ''
      const uniqueKey = `${productId}_${productName}`

      if (!seenIds.has(uniqueKey)) {
        seenIds.add(uniqueKey)
        uniqueProducts.push(product)
      }
    })
    return uniqueProducts
  }

  getSuggestionListNames = () => {
    return (
      this.suggestionList.map?.(product => product?.properties?.name || '') ||
      []
    )
  }

  /**
   * below functions is for search value update
   * and redirect to the search page
   */
  getSearchSuggestions = async (handleFocus = '') => {
    catalogDeps.isPlainSearch = false
    if (
      this.searchValue !== '' &&
      this.searchValue?.length >= minimumSearchCharLength
    ) {
      let searchTermForApi = this.searchValue
      const response = await catalogContainer.getSearchSuggestions(
        this.searchValue,
        1,
        this.searchLimit
      )
      const isSameSearchValue = searchTermForApi === this.searchValue
      // CX26-2169
      // when multiple characters are typed at same time
      // sometimes previous API's response is set due to API latencies / response time
      //  so we are comparing the latest search value
      if (isSameSearchValue) {
        this.suggestionResponse = response
        if (response?.product && response?.product?.length) {
          this.suggestionList = this.getSuggestionList(response?.product)
          const totalCount = response?.pageableInfo?.totalCount

          if (!handleFocus) {
            this.limitSuggestion = this.suggestionList?.slice(
              0,
              this.searchLimit
            )
            if (totalCount > this.limitSuggestion?.length) {
              this.showMore = true
            } else {
              this.showMore = false
            }
          }

          //-------- DataLayer object to be sent to GTM -----------------

          trackSearchSuggestions({
            searchTerm: this.searchValue,
            searchResult: this.getSuggestionListNames(),
          })

          !this.props.searchCleared && this.props.clearSearchModal(false)
        } else {
          this.resetData()
        }

        this.didYouMean =
          // isSameSearchValue &&
          response?.didYouMean ? response?.didYouMean : []
        if (this.didYouMean?.length > 0) {
          this.resetData()
        }
      }
    } else {
      this.resetData()
      this.didYouMean = []
    }
  }

  resetData = () => {
    this.limitSuggestion = []
    this.searchLimit = 6
    this.indexLimit = 6
    this.showMore = false
    this.suggestionList = []
    this.suggestionResponse = {}
    this.hasUserTyped = false
  }
  isJsonString = str => {
    try {
      JSON.parse(str)
    } catch (e) {
      return false
    }
    return true
  }
  handleSuggestionVisibility = () => {
    const isDropdownVisible = this.handleDropdownVisibility()
    if (isDropdownVisible) {
      this.isSuggestionVisible = true
    }
  }
  handleChange = async event => {
    if (!application.isDesktopOrLarger && this.props?.nuskinReadOnly) {
      this.searchValue = ''
      return
    }

    // regex pattern for zero width space and tab encode values
    var re = /(%E2%80%86|%09)/g
    let result = encodeURIComponent(event?.target?.value || '')
    let searchVal = decodeURIComponent(result?.replace(re, ' ')?.trim() || '')
    this.searchValue = searchVal
    this.userEnteredValue = this.searchValue
    this.hasUserTyped = this.searchValue.length > 0
    this.latestSuggestionsPromise = this.getSearchSuggestions()
    this.searchValue ? this.announceSearchResults() : this.clearAnnouncement()
    await this.latestSuggestionsPromise
    if (this.isSearchFocused) {
      /*
       * https://nuskin.atlassian.net/browse/CX26-1392
       * when multiple characters are typed and ENTER key is hit,
       * when suggestion call has latency, the search dropdown opens up in search page
       * so opening the searchbox only when the textbox is focused
       */
      this.handleSuggestionVisibility()
    }
  }

  handleFocus = async e => {
    if (!application.isDesktopOrLarger && this.props?.nuskinReadOnly) {
      e?.target?.blur()
      return
    }
    let handleFocus = true
    await this.getSearchSuggestions(handleFocus)
    if (this.isSearchFocused) {
      /*
       * https://nuskin.atlassian.net/browse/CX26-1392
       * when multiple characters are typed and ENTER key is hit,
       * when suggestion call has latency, the search dropdown opens up in search page
       * so opening the searchbox only when the textbox is focused
       */
      this.handleSuggestionVisibility()
    }
  }

  handleSearchClick = () => {
    if (this.props?.isNuskinHeader && this.props?.toggleSearchModal) {
      this.props?.toggleSearchModal(true)
    }
  }

  // hideSearchModal = () => {
  //   if (this.props?.isNuskinHeader && this.props?.toggleSearchModal) {
  //     this.props?.toggleSearchModal(false)
  //   }
  // }

  checkDuplicateValue = searchValue => {
    const list =
      this.recentSearch?.filter(listValue => {
        return listValue === searchValue
      }) || []
    const isUniqueSearchValue = list.length > 0 ? true : false
    return isUniqueSearchValue
  }
  handleRecentSearch = () => {
    const isValid = this.checkDuplicateValue(this.searchValue)
    const indexOfDuplicate = this.recentSearch?.indexOf(this.searchValue)
    !isValid && this.recentSearch?.unshift(this.searchValue)
    isValid && this.recentSearch?.splice(indexOfDuplicate, 1)
    isValid && this.recentSearch?.unshift(this.searchValue)
    this.recentSearch = this.recentSearch?.slice(0, this.limitRecentSearch)
    setLocalStorage('recentSearch', this.recentSearch)
  }
  handleSubmit = event => {
    event.preventDefault()
    if (this.searchValue !== '' && this.searchValue) {
      catalogDeps.isPlainSearch = false
      if (event?.type == 'click') {
        trackSearchResult({
          searchType: 'fullSearch',
          searchTerm: this.searchValue,
        })
      } else {
        catalogDeps.isPlainSearch = true
      }

      const isRedesignEnabled = convertToBoolean(
        APPConfig?.getAppConfig()?.enableNewHeaderNavigation
      )
      const encodedURL = encodeURIComponent(this.searchValue) || ''
      this.props?.history?.push(`${pageNames.search}/${encodedURL}`)
      this.handleRecentSearch()
      if (!isRedesignEnabled) {
        this.searchValue = ''
        this.userEnteredValue = ''
        this.dropDownSelectedValue = ''
        if (this.refSearchInput) {
          this.refSearchInput.value = ''
        }
      }

      this.isSuggestionVisible = false
      this.isSearchFocused = false
      this.props?.closeSearchModal?.()
    }
  }

  removeSelectedSearch = (item, event) => {
    event.persist()
    this.recentSearch?.map((listItem, index) => {
      if (listItem === item) {
        this.recentSearch.splice(index, 1)
        setLocalStorage('recentSearch', this.recentSearch)
      }
    })
    if (this.recentSearch?.length === 0 && this.suggestionList?.length === 0) {
      this.isSuggestionVisible = false
    }
  }

  handleRecentSearchList = () => {
    const isRedesignEnabled = convertToBoolean(
      APPConfig?.getAppConfig()?.enableNewHeaderNavigation
    )
    // cache searchData locally to avoid multiple deep-clone operations in one render
    const searchData = this.getSearchData()
    if (isRedesignEnabled) {
      const maxVisibleChips = 6
      const visibleChips = this.recentSearch.slice(0, maxVisibleChips)
      const remainingChips = this.recentSearch.slice(maxVisibleChips)
      const chipRenderer = (item, key) => {
        const handleDropDownChange = () => {
          this.searchValue = item
          this.handleRecentSearch()
          this.isSuggestionVisible = false
          this.isSearchFocused = false
          searchState.setSearchFocused(false)
          this.props?.closeSearchModal?.()
          if (this.enableLiveEvent === 'true') {
            trackRecentSearchEvent({
              searchTerm: item,
              recentSearchList: this.recentSearch.map(x => x),
            })
          }
          trackRecentSearch({ searchTerm: item })
        }
        const encodedURL = encodeURIComponent(item)
        return (
          <Dropdown.Item
            key={key}
            to={`${pageNames.search}/${encodedURL}`}
            as={Link}
            tabIndex={0}
            onClick={handleDropDownChange}
            className="recent-Chip">
            {item}
          </Dropdown.Item>
        )
      }
      const visibleList = visibleChips.map((item, i) =>
        chipRenderer(item, i + item)
      )
      const remainingChipsNodes = this.state.showAllChips
        ? remainingChips.map((item, idx) =>
            chipRenderer(item, maxVisibleChips + idx + item)
          )
        : []
      const moreChip =
        remainingChips.length > 0 && !this.state.showAllChips ? (
          <Dropdown.Item
            key="more-chip"
            tabIndex={0}
            onClick={() => this.setState({ showAllChips: true })}
            className="recent-Chip">
            {`+${remainingChips.length} ${searchData?.more || ''}`}
          </Dropdown.Item>
        ) : null
      return (
        <div className="All-chips">
          {[...visibleList, moreChip, ...remainingChipsNodes]}
        </div>
      )
    }
    return this.recentSearch?.map((item, index) => {
      const handleDropDownChange = () => {
        this.searchValue = item
        this.handleRecentSearch()
        this.isSuggestionVisible = false
        this.isSearchFocused = false
        searchState.setSearchFocused(false)
        this.props?.closeSearchModal?.()
        if (this.enableLiveEvent === 'true') {
          trackRecentSearchEvent({
            searchTerm: item,
            recentSearchList: this.recentSearch.map(v => v),
          })
        }
        trackRecentSearch({ searchTerm: item })
      }
      const encodedURL = encodeURIComponent(item)
      return (
        <div key={item}>
          <Dropdown.Item
            key={index + item}
            to={`${pageNames.search}/${encodedURL}`}
            as={Link}
            tabIndex={0}
            onClick={handleDropDownChange}
            className="search-item-pill">
            {item}
          </Dropdown.Item>
        </div>
      )
    })
  }

  getSearchData = () => {
    try {
      return customerContainer.searchData
        ? JSON.parse(JSON.stringify(customerContainer.searchData))
        : null
    } catch {
      return null
    }
  }
  getPdtImage = (product = {}) => {
    // if (isMerchPLP) {
    //   const { currentIndex, images } = this.imageArrayProperties
    //   return images[currentIndex]
    // } else
    if (product?.type == 'kit' || product?.type === 'bundle') {
      return getImageFromProperty(product?.properties)
    }
    const defaultSku = catalogDeps.getDefaultSelectedSku(product)
    return getImageFromProperty(defaultSku?.[0]?.properties)
  }
  /* reverting as per latest comment CX12-9374 */
  /* getURLforRedirecting = (suggestion, isFromDidYouMean, parsedItem = '') => {
    /*
     * https://nuskin.atlassian.net/browse/CX12-9374
     * If the user clicks on the did you mean suggestion, it should take them to the search results page
     * else if they click on product name, it should take them to the product details page
     * 
    let url = ''
    if (isFromDidYouMean) {
      const encodedURL =
        encodeURIComponent(encodeURIComponent(parsedItem)) || ''
      url = `${pageNames.search}/${encodedURL}`
    } else {
      /*
       * constructing the url for product details page with variant
       * to be changed to SKUID later when slug is not present
       * @variant_autoselect - redundant/can be ignored based on CX12-9374
       * 
      /* update: this code is redundant now,
       * as per latest comments,
       * when we click on product suggestion,
       * redirection always happens to search page and not to PDP 
       * 
      const slug = suggestion?.properties?.slug || ''
      const identifier = suggestion?.properties?.identifier || ''
      const productOrSlugId = slug ? slug : identifier
      const defaultSku = catalogDeps.getDefaultSelectedSku(suggestion)
      const skuSlug = defaultSku?.[0]?.properties?.slug || ''
      const skuId = defaultSku?.[0]?.identifier || ''
      const hasMoreThanOneSku = (suggestion?.sku?.length || 1) > 1
      const variantParameter = skuSlug || skuId || ''

      url = `${pageNames.product}/${productOrSlugId}`
      /* @variant_autoselect *
      if (hasMoreThanOneSku && variantParameter) {
        url += `?variant=${variantParameter}`
      }
    }
    return url
  }*/

  handleQuickView = (
    e,
    idToFocus,
    replacementProductStatus = false,
    product
  ) => {
    const handleFocus = () => {
      if (this.currentRef && e.code === 'Enter') {
        setTimeout(
          () => this.currentRef.current.focus({ focusVisible: true }),
          500
        )
      }
    }
    e.preventDefault()
    e.stopPropagation()
    const productId =
      product?.properties?.slug ||
      product?.productId ||
      product?.identifier ||
      ''

    const categoryId = product?.categoryId || ''
    const defaultSku = catalogDeps.getDefaultSelectedSku(product)
    const skuSlug = defaultSku?.[0]?.properties?.slug || ''
    const skuId = defaultSku?.[0]?.identifier || ''
    const hasMoreThanOneSku = (product?.sku?.length || 1) > 1
    const variantParameter = skuSlug || skuId || ''
    const isMultiVariant = defaultSku?.[0]?.properties?.variantLabel || ''

    /* @variant_autoselect */
    quickViewState.getProduct({
      productId,
      categoryId,
      handleFocus,
      defaultVariantForQuickView: variantParameter,
      isMultiVariant: isMultiVariant,
    })
  }

  handleProductCart = async (options = {}) => {
    const { product, url } = options

    try {
      const isRedirectToPdp = await shouldRedirectToPdp(product?.identifier)

      if (isRedirectToPdp && url) {
        this.props.history.push(url)
      } else {
        searchDeps.handleAddtoCart(product)
      }
    } catch (error) {
      console.error('Failed to fetch product:', error)
    }
  }

  renderQuickView = index => {
    const isDesktop = getDeviceType() === 'desktop'
    const product = this.limitSuggestion[index]
    const buttonDisabled = convertToBoolean(
      searchDeps.isAddToCartDisabled(product)
    )
    const categoryId = product?.categoryId || ''
    const identifier = product?.productId || product?.identifier || ''
    const defaultSku = catalogDeps.getDefaultSelectedSku(product)
    const replacementProductStatus =
      product?.sku?.[0]?.properties?.productStatus?.toLowerCase?.().trim() ===
      'discontinued'
    const slug = product?.properties?.slug
    const skuId = defaultSku?.[0]?.identifier || ''
    const hasMoreThanOneSku = (product?.sku?.length || 1) > 1
    const productOrSlugId = slug ? slug : identifier
    const skuSlug = defaultSku?.[0]?.properties?.slug || ''
    const variantParameter = skuSlug || skuId || ''
    /* @variant_autoselect */
    const shouldAppendVariant = hasMoreThanOneSku && variantParameter
    let url = ''
    if (categoryId && shouldAppendVariant) {
      url = `${pageNames.product}/${productOrSlugId}?categoryId=${categoryId}&variant=${variantParameter}`
    } else if (categoryId) {
      url = `${pageNames.product}/${productOrSlugId}?categoryId=${categoryId}`
    } else if (shouldAppendVariant) {
      url = `${pageNames.product}/${productOrSlugId}?variant=${variantParameter}`
    } else {
      url = `${pageNames.product}/${productOrSlugId}`
    }
    const isSeparateAddToCartEnabled = convertToBoolean(
      APPConfig?.getAppConfig()?.enableSearchAddToCart
    )

    const renderButton = () => {
      const cartIconCheck =
        !isSeparateAddToCartEnabled || shouldRedirectBasedOnProduct(product)
      const shouldDisableAddToCart = !cartIconCheck && buttonDisabled
      return (
        <Button
          className="search-quick-view-btn bg-transparent"
          disabled={shouldDisableAddToCart}
          aria-label={
            cartIconCheck
              ? i18nTranslate('plp.quickView', 'Quick View')
              : i18nTranslate('addToCart', 'Add to cart')
          }
          data-testid="qa-search-quick-view"
          onClick={e => {
            if (cartIconCheck) {
              this.props.history.push(url)
              this.props?.closeSearchModal?.()
            } else {
              searchDeps.handleAddtoCart(product)
              this.props?.closeSearchModal?.()
            }
          }}
          onKeyDown={e => {
            if (e?.keyCode === 13) {
              if (cartIconCheck) {
                this.props.history.push(url)
              } else {
                this.handleAddToCartClick(e, isDesktop)
              }
            } else {
              this.handleKeyDown(e)
            }
          }}>
          <span>
            {cartIconCheck ? <NuskinTaskIcon /> : <NuskinPlusCartIcon />}
          </span>
        </Button>
      )
    }
    return (
      <div>
        {/* {isDesktop
          ? this.hoverIndex === index || this.dropDownSelectedIdx === index
            ? renderButton()
            : null
          : renderButton()} */}
        {renderButton()}
      </div>
    )
  }

  handleMouseEnter = index => {
    this.hoverIndex = index
  }

  handleMouseLeave = () => {
    this.hoverIndex = null
  }
  handleAddToCartClick = async (e, isDesktop) => {
    const product =
      this.suggestionList[
        this.hoverIndex !== null ? this.hoverIndex : this.dropDownSelectedIdx
      ]
    searchDeps.handleAddtoCart(product)
    this.isActive = false
    this.disableListener()
    // As per legacy site we are closing only the search box/dropdown and not removing the search keyword
    if (isDesktop) {
      this.handleOutsideClick(e, true)
    } else {
      this.props?.closeSearchModal?.()
    }
  }
  renderSuggestionList = (suggestion, index, isFromDidYouMean = false) => {
    const name = isFromDidYouMean
      ? suggestion || ''
      : suggestion?.properties?.name || ''

    const handleDropDownChange = () => {
      this.searchValue = name
      this.handleRecentSearch()
      this.isSuggestionVisible = false
      this.isSearchFocused = false
      this.props?.closeSearchModal?.()

      let product = {}

      if (this.suggestionResponse?.product) {
        product = this.suggestionResponse?.product?.[index]
      }

      if (this.enableLiveEvent === 'true') {
        trackPredictiveSearchEvent({
          typedQuery: this.userEnteredValue,
          searchTerm: name,
          product,
          suggestionProductList: this.getSuggestionListNames(),
        })
      }
      trackPredictiveSearch({
        typedQuery: this.userEnteredValue,
        searchTerm: name,
        product,
      })
    }
    const parsedItem = name || ''
    const encodedURL = encodeURIComponent(parsedItem) || ''
    /* reverting as per latest comment CX12-9374 */
    /* let url = this.getURLforRedirecting(
      suggestion,
      isFromDidYouMean,
      parsedItem
    )*/
    const imageURL = this.getPdtImage(suggestion)
    const showSearchSuggestionImage =
      APPConfig?.getAppConfig()?.showSearchSuggestionImage === 'true'
    const isToEnableAddToCart = convertToBoolean(
      APPConfig?.getAppConfig()?.enableSearchAddToCart
    )
    const categoryId = suggestion?.categoryId || ''
    const identifier = suggestion?.productId || suggestion?.identifier || ''
    const defaultSku = catalogDeps.getDefaultSelectedSku(suggestion)
    const replacementProductStatus =
      suggestion?.sku?.[0]?.properties?.productStatus
        ?.toLowerCase?.()
        .trim() === 'discontinued'
    const slug = suggestion?.properties?.slug
    const skuId = defaultSku?.[0]?.identifier || ''
    const hasMoreThanOneSku = (suggestion?.sku?.length || 1) > 1
    const productOrSlugId = slug ? slug : identifier
    const skuSlug = defaultSku?.[0]?.properties?.slug || ''
    const variantParameter = skuSlug || skuId || ''
    /* @variant_autoselect */
    const shouldAppendVariant = variantParameter
    let url = ''
    if (isFromDidYouMean) {
      url = `${pageNames.search}/${encodeURIComponent(encodeURIComponent(suggestion))}`
    } else if (categoryId && shouldAppendVariant) {
      url = `${pageNames.product}/${productOrSlugId}?categoryId=${categoryId}&variant=${variantParameter}`
    } else if (categoryId) {
      url = `${pageNames.product}/${productOrSlugId}?categoryId=${categoryId}`
    } else if (shouldAppendVariant) {
      url = `${pageNames.product}/${productOrSlugId}?variant=${variantParameter}`
    } else {
      url = `${pageNames.product}/${productOrSlugId}`
    }

    return (
      parsedItem && (
        <Dropdown.Item
          key={index + parsedItem}
          /* reverting as per latest comment CX12-9374 */
          /* to={url} */
          // to={`${pageNames.search}/${encodedURL}`}
          // as={Link}
          // default `as` (SafeAnchor) injects role="button", doubling AT announcement with the nested quick-view button
          as="div"
          tabIndex={-1}
          className={
            this.dropDownSelectedIdx == index ? 'dropdown-item-selected' : ''
          }
          onMouseEnter={() => this.handleMouseEnter(index)}
          onMouseLeave={this.handleMouseLeave}
          onKeyDown={e => {
            if (e?.keyCode === 27) {
              // ESC key
              this.handleEscape(e)
            } else {
              this.handleKeyDown(e)
            }
          }}>
          <div className="d-flex align-items-center search-suggestion-wrapper">
            <Link
              to={`${url}`}
              onClick={handleDropDownChange}
              className={`d-flex align-items-center image-text-wrapper ${
                showSearchSuggestionImage ? 'suggestion-wrapper-with-image' : ''
              }`}
              tabIndex={0}
              aria-label={parsedItem}
              data-testid="qa-search-suggestion">
              {!isFromDidYouMean && showSearchSuggestionImage && (
                <ImageComponent
                  testid="qa-product-search-image"
                  overrideParentClass={true}
                  customParentClassName="product-search-wrapper"
                  className="product-search-image"
                  src={imageURL}
                  alt={parsedItem}
                  optimizeImage={true}
                  height={'50px'}
                  width={'50px'}
                />
              )}
              <div className="search-text-display">{parsedItem}</div>
            </Link>
            {!isFromDidYouMean && this.renderQuickView(index)}
          </div>
        </Dropdown.Item>
      )
    )
  }

  clearAllSearch = () => {
    setLocalStorage('recentSearch', [])
    this.recentSearch = []
    this.removeSearch()
  }

  renderSuggestion() {
    const isRedesignEnabled = convertToBoolean(
      APPConfig?.getAppConfig()?.enableNewHeaderNavigation
    )
    // cache search data once per renderSuggestion invocation
    const searchData = this.getSearchData()
    const { isNuskinHeader = false } = this.props
    const recentSearchView = this.handleRecentSearchList()
    const isToHighlightShowMore =
      this.dropDownSelectedIdx == this.getCurrentlyDisplayedArray()?.length
    const cancelButton = (
      <Button
        className="search-close-button d-none d-lg-block position-absolute border-0"
        variant=""
        onClick={this.removeSearch}
        style={{
          width: '134px',
          height: '44px',
          gap: '10px',
          paddingRight: '16px',
          paddingLeft: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <CloseIcon
          size="1.5rem"
          className="text-dark"
          aria-label={
            this.props?.clearInputAriaLabel ||
            i18nTranslate('search.clearInput', 'clear input', {
              nameSpace: 'ssr-resource',
            })
          }
          data-testid="qa-search-hide-icon"
        />
        <span>
          {searchData?.cancel_search ||
            i18nTranslate('search.cancel', 'Cancel', {
              nameSpace: this.nameSpace,
            })}
        </span>
      </Button>
    )
    // Recent searches content - only visible when conditions are met
    const recentSearchBlock = (
      <>
        {recentSearchView &&
          !isRedesignEnabled &&
          recentSearchView.length > 0 && (
            <>
              <Dropdown.Header className="mt-3">
                <div className="recent-search-title">
                  {i18nTranslate('recent.search', 'Your Recent Searches', {
                    nameSpace: 'ssr-resource',
                  })}
                </div>
                <div className="recent-search-clear">
                  <Button
                    className="recent-search-clear-btn"
                    onClick={this.clearAllSearch}
                    tabIndex="0">
                    {i18nTranslate('search.clearall', 'Clear All', {
                      nameSpace: 'ssr-resource',
                    })}
                  </Button>
                </div>
              </Dropdown.Header>
              <div className="recent-search-row">{recentSearchView}</div>
            </>
          )}
        {isRedesignEnabled && recentSearchView && (
          <>
            <Dropdown.Header className="px-3 quickOpts">
              <div className="recent-search-title">
                {searchData?.clear_recent_search ||
                  i18nTranslate('recent.search', 'Your Recent Searches', {
                    nameSpace: 'ssr-resource',
                  })}
              </div>
              <div className="recent-search-clear">
                <Button
                  className="recent-search-clear-btn"
                  onClick={this.clearAllSearch}
                  tabIndex="0">
                  {searchData?.clear_all ||
                    i18nTranslate('search.clearall', 'Clear All', {
                      nameSpace: 'ssr-resource',
                    })}
                </Button>
              </div>
            </Dropdown.Header>
            <div>{recentSearchView}</div>
          </>
        )}
      </>
    )

    const shouldShowRecentSearchBlock =
      !this.hasUserTyped &&
      this.searchValue?.length === 0 &&
      this.limitSuggestion?.length === 0 &&
      this.didYouMean?.length === 0 &&
      this.recentSearch?.length > 0

    const suggestionView = (
      <Dropdown.Menu
        id="suggestionDropdown"
        className={`${
          isNuskinHeader ? 'search-box-modal' : 'search-suggestion-dropdown'
        }`}
        show={this.isSuggestionVisible}
        onFocus={() => {
          this.isSearchFocused = true
          this.isSuggestionVisible = true
        }}
        onBlur={event => {
          if (!event?.currentTarget?.contains(event?.relatedTarget)) {
            this.isSearchFocused = false
            this.isSuggestionVisible = false
          }
        }}
        data-testid="qa-search-suggestion">
        {cancelButton}
        {this.limitSuggestion?.length > 0 && (
          <div className="search-inner" id="search-inner-element">
            {this.limitSuggestion?.map((product, index) => {
              return this.renderSuggestionList(product, index)
            })}
          </div>
        )}
        {this.didYouMean.length > 0 &&
          (isNuskinHeader ? (
            <>
              <div className="mt-2">
                <div className="nu-popular-searches">
                  <div className="font-weight-bold px-3 h-100 nu-popular-searches-innerbox">
                    {/*i18nTranslate('search.popularSearches', 'Popular searches')*/}
                    {/* CX121-2016 */}
                    {searchData?.did_you_mean ||
                      i18nTranslate('search.didyoumean', 'Did You Mean?', {
                        nameSpace: this.nameSpace,
                      })}
                  </div>
                </div>
                <div>
                  {this.didYouMean.map((prompt, index) => {
                    return this.renderSuggestionList(prompt, index, true)
                  })}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="font-weight-bold px-3">
                {searchData?.did_you_mean ||
                  i18nTranslate('search.didyoumean', 'Did You Mean?', {
                    nameSpace: this.nameSpace,
                  })}
              </div>
              <div>
                {this.didYouMean.map((prompt, index) => {
                  return this.renderSuggestionList(prompt, index, true)
                })}
              </div>
            </>
          ))}
        {this.showMore &&
          (isNuskinHeader ? (
            <div
              className={`nu-show-more px-3 d-flex align-middle w-100 ${
                isToHighlightShowMore && 'dropdown-item-selected'
              }`}>
              <button
                className="nu-show-more-button justify-content-center w-100 d-flex"
                tabIndex="0"
                // Pointerdown will affect ADA don't use it
                // onPointerDown={this.loadMore}
                onClick={this.handleSubmit}
                data-testid="qa-search-suggestion-show-more">
                {searchData?.see_more_results ||
                  i18nTranslate('search.seeMoreResults', 'See More Results', {
                    nameSpace: this.nameSpace,
                  })}
              </button>
            </div>
          ) : (
            <button
              className="search-loadmore"
              tabIndex="0"
              // onPointerDown={this.loadMore}
              onClick={this.loadMore}>
              {searchData?.show_more ||
                i18nTranslate('search.showmore', 'Show More', {
                  nameSpace: this.nameSpace,
                })}
            </button>
          ))}
        {shouldShowRecentSearchBlock &&
          (isNuskinHeader ? <div>{recentSearchBlock}</div> : recentSearchBlock)}
      </Dropdown.Menu>
    )

    return suggestionView
  }

  loadMore = async () => {
    this.isSuggestionVisible = true
    let newIndex = this.indexLimit + this.searchLimit
    TagManager.dataLayer({
      dataLayer: {
        event: 'searchLoadMoreButtonUsed',
        searchType: 'searchLoadMoreButtonUsed',
        typedQuery: 'textSearch',
        pageTitle: 'Home Page',
        query: 'textSearch',
        resultObject: {
          userName:
            customerContainer?.profileResponse?.userName &&
            customerContainer?.profileResponse?.userName != ''
              ? customerContainer?.profileResponse?.userName
              : 'Guest User',
          userAccountId:
            customerContainer?.accountId > 0
              ? customerContainer?.accountId
              : 'Guest Account',
        },
        searchMode: 'custom',
        platform: 'equinox',
      },
    })
    const page = newIndex ? newIndex / 6 : 1
    await catalogContainer
      .getSearchSuggestions(this.searchValue, page, this.searchLimit)
      .then(response => {
        this.suggestionList = this.getSuggestionList(response.product)
        const totalCount = response?.pageableInfo?.totalCount
        const newShowMore = newIndex < 50
        let limitSuggestionLength = this.limitSuggestion?.length
        let suggestionListLength = this.suggestionList?.length
        let newList = []
        if (limitSuggestionLength + suggestionListLength > totalCount) {
          let diff = totalCount - limitSuggestionLength
          newList = this.limitSuggestion?.concat(
            this.suggestionList.slice(0, diff)
          )
        } else {
          newList = this.limitSuggestion?.concat(this.suggestionList)
        }
        this.indexLimit = newIndex
        this.limitSuggestion = newList
        if (totalCount === this.limitSuggestion?.length) {
          this.showMore = false
          this.indexLimit = 6
          newIndex = 0
        }
        // after load more content is loaded, element was hidden at bottom
        // auto scroll wasn't working as expected
        if (IS_BROWSER) {
          setTimeout(() => {
            const scrollBehavior = {
              behavior: 'smooth',
              block: 'center',
              inline: 'start',
            }

            document
              .querySelector(
                '#suggestionDropdown > div.search-inner > .dropdown-item-selected.dropdown-item'
              )
              ?.scrollIntoView(scrollBehavior)
          }, 300)
        }
      })
  }

  removeSearch = () => {
    this.isSuggestionVisible = false
    this.isSearchFocused = false
    this.searchValue = ''
    this.hasUserTyped = false
    this.clearAnnouncement()
    searchState.setSearchFocused(false)
    this.props.clearSearchModal(true)
  }

  setSearchBoxRef = input => {
    if (input) {
      this.refSearchInput = input

      if (this.props.shouldFocusOnRender) {
        requestAnimationFrame(() => {
          this.refSearchInput?.focus()
        })
      }
    }
  }

  clearInputHandler = () => {
    this.searchValue = ''
    this.hasUserTyped = false
    this.isSearchFocused = false
    this.isActive = false
    this.clearAnnouncement()
    searchState.setSearchFocused(false)
    this.props.clearSearchModal(true)
    this.isSuggestionVisible = false
    if (this.refSearchInput?.focus) {
      this.refSearchInput.focus()
    }
  }

  handleMaskClick = e => {
    this.handleOutsideClick(e, true)
  }

  handleGlobalKeyDown = e => {
    const escapeCode = 27
    // Only handle ESC key when search suggestions are visible
    if (e?.keyCode === escapeCode && this.isSuggestionVisible) {
      this.handleEscape(e)
    }
  }

  handleKeyDown = e => {
    const upArrowCode = 38
    const downArrowCode = 40
    const enterCode = 13
    const escapeCode = 27

    if (e?.keyCode == enterCode) {
      this.handleSearchEnter(e)
    } else if (e?.keyCode == upArrowCode) {
      this.handleUpArrow(e)
    } else if (e?.keyCode == downArrowCode) {
      this.handleDownArrow(e)
    } else if (e?.keyCode == escapeCode) {
      this.handleEscape(e)
    }
  }

  handleSearchEnter = e => {
    let currentlyDisplayedArray = this.getCurrentlyDisplayedArray()
    if (this.dropDownSelectedIdx == currentlyDisplayedArray.length) {
      e?.preventDefault?.()
      e?.stopPropagation?.()
      this.loadMore()
    } else {
      this.handleSubmit(e)
    }
    /* reverting as per latest comment CX12-9374 */

    /* else if (this.dropDownSelectedIdx != -1) {
      /*
       * added fix for dropdown selection using enter key
       * https://nuskin.atlassian.net/browse/CX12-9374
       * If the user clicks on the did you mean suggestion, it should take them to the search results page
       * else if they click on product name, it should take them to the product details page
       
      let currentlyDisplayedArray = this.getCurrentlyDisplayedArray()
      let currentItem = currentlyDisplayedArray?.[this.dropDownSelectedIdx]
      let isProduct = currentItem?.identifier ? true : false
      let url = ''
      if (isProduct) {
        url = this.getURLforRedirecting(currentItem, false)
      } else {
        url = this.getURLforRedirecting(currentItem, true, this.searchValue)
        this.handleRecentSearch()
      }
      this.props?.history?.push(url)
      this.isSuggestionVisible = false
      this.isSearchFocused = false
      this.props?.closeSearchModal?.()
      e?.preventDefault?.()
      e?.stopPropagation?.()
    }*/
  }

  handleEscape = e => {
    e?.preventDefault?.()
    e?.stopPropagation?.()

    // Close search suggestions and reset dropdown selection
    this.isSuggestionVisible = false
    this.isSearchFocused = false
    this.isActive = false
    this.dropDownSelectedIdx = -1
    this.hoverIndex = null

    // Reset search state
    searchState.setSearchFocused(false)

    // Clear search text and hide close icon on ESC key (same as outside click)
    this.searchValue = ''
    this.userEnteredValue = ''
    this.dropDownSelectedValue = ''

    // Clear the DOM input value directly
    if (this.refSearchInput) {
      this.refSearchInput.value = ''
    }

    // Reset suggestion data
    this.resetData()
    this.didYouMean = []
    this.clearAnnouncement()

    // Close search modal and disable listener
    this.props.clearSearchModal(true)
    this.disableListener()

    // Remove focus from input to fully close the search
    if (this.refSearchInput) {
      this.refSearchInput.blur()
    }
  }

  handleUpArrow = e => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    if (this.dropDownSelectedIdx != -1) {
      this.dropDownSelectedIdx = this.dropDownSelectedIdx - 1
    }
    this.setSelectedValue(e)
  }

  handleDownArrow = e => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    let currentlyDisplayedArray = this.getCurrentlyDisplayedArray()
    if (this.dropDownSelectedIdx < currentlyDisplayedArray.length - 1) {
      this.dropDownSelectedIdx = this.dropDownSelectedIdx + 1
    } else if (
      this.showMore &&
      this.dropDownSelectedIdx != currentlyDisplayedArray.length
    ) {
      this.dropDownSelectedIdx = currentlyDisplayedArray.length
      this.searchValue = this.userEnteredValue
    }
    this.setSelectedValue(e)
  }

  getCurrentlyDisplayedArray = () => {
    let currentlyDisplayedArray = this.limitSuggestion
    if (
      this.recentSearch.length > 0 &&
      this.limitSuggestion.length <= 0 &&
      this.didYouMean.length == 0
    ) {
      currentlyDisplayedArray = this.recentSearch
    } else if (this.didYouMean.length > 0) {
      currentlyDisplayedArray = this.didYouMean
    }
    return currentlyDisplayedArray
  }
  isProductArrayDisplayedCurrently = () => {
    let isProductArrayDisplayed = true
    if (
      this.recentSearch.length > 0 &&
      this.limitSuggestion.length <= 0 &&
      this.didYouMean.length == 0
    ) {
      isProductArrayDisplayed = false
    } else if (this.didYouMean.length > 0) {
      isProductArrayDisplayed = false
    }
    return isProductArrayDisplayed
  }
  setSelectedValue = e => {
    const scrollBehavior = {
      behavior: 'smooth',
      block: 'center',
      inline: 'start',
    }
    let currentlyDisplayedArray = this.getCurrentlyDisplayedArray()

    this.dropDownSelectedValue =
      currentlyDisplayedArray.length > this.dropDownSelectedIdx
        ? currentlyDisplayedArray[this.dropDownSelectedIdx]
        : ''
    if (this.dropDownSelectedValue) {
      this.searchValue =
        this.dropDownSelectedValue?.properties?.name ||
        this.dropDownSelectedValue ||
        ''
    }

    e?.preventDefault?.()
    e?.stopPropagation?.()
    if (IS_BROWSER) {
      document
        .querySelector(
          '#suggestionDropdown > div.search-inner > .dropdown-item-selected.dropdown-item'
        )
        ?.scrollIntoView(scrollBehavior)
    }
  }

  render() {
    const isRedesignEnabled = convertToBoolean(
      APPConfig?.getAppConfig()?.enableNewHeaderNavigation
    )
    const { placeHolder = '', isNuskinHeader = false } = this.props
    const isShowSearchDropDown = this.handleDropdownVisibility()
    const highLightSearchClass =
      isNuskinHeader && this.isSearchFocused ? 'search-highlight' : ''
    const clearInputAriaLabel =
      this.props?.clearInputAriaLabel ||
      i18nTranslate('search.clearInput', 'clear input', {
        nameSpace: 'ssr-resource',
      })
    const navigationSearchAriaLabel =
      this.props?.navigationSearchAriaLabel ||
      i18nTranslate('search.navigationSearch', 'navigation search', {
        nameSpace: 'ssr-resource',
      })

    const coreSection = (
      <section
        className={`search-group d-flex flex-column flex-grow-1 ${
          this.isSuggestionVisible && 'search-suggestion-visible'
        }`}
        ref={this.setRef}
        onFocus={this.handleBoundaryFocus}
        onBlur={this.handleBoundaryBlur}>
        {<QuickView />}
        <div
          className="sr-only"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          data-testid="qa-search-results-announcement">
          {this.searchAnnouncement}
        </div>
        <Form
          className={`${
            isNuskinHeader ? 'nu-search-box' : 'search-box'
          } ${highLightSearchClass}`}
          onSubmit={this.handleSubmit}
          data-testid="qa-search">
          <InputGroup className="flex-nowrap">
            <SearchInputGroupContent
              searchValue={this.searchValue}
              isNuskinHeader={isNuskinHeader}
              isRedesignEnabled={isRedesignEnabled}
              onClearInput={this.clearInputHandler}
              onSubmit={this.handleSubmit}
              clearInputAriaLabel={clearInputAriaLabel}
              navigationSearchAriaLabel={navigationSearchAriaLabel}>
              <RenderForm
                value={this.searchValue}
                onFocus={this.handleFocus}
                onChange={this.handleChange}
                placeHolder={placeHolder}
                onKeyDown={this.handleKeyDown}
                removeSearch={this.removeSearch}
                isNuskinHeader={isNuskinHeader}
                onClick={this.handleSearchClick}
                isRedesignEnabled={isRedesignEnabled}
                searchData={this.getSearchData()}
                ref={this.setSearchBoxRef}
              />
            </SearchInputGroupContent>
          </InputGroup>
        </Form>
        {isShowSearchDropDown && this.renderSuggestion()}
        {this.isSuggestionVisible && !isNuskinHeader && (
          <div className="search-mask"></div>
        )}
        {this.isSearchFocused &&
          isNuskinHeader &&
          application.isDesktopOrLarger && (
            <div className="search-mask" onClick={this.handleMaskClick}></div>
          )}
      </section>
    )

    return isRedesignEnabled ? (
      <div className="search-redesign">
        <RedesignStyles>{() => null}</RedesignStyles>
        {coreSection}
      </div>
    ) : (
      coreSection
    )
  }
}

export default SearchBox
export { SearchBox, RenderForm, SearchInputGroupContent }
